import util from '../util';
import Matter from 'matter-js';
import decomp from 'poly-decomp';
import Widget from './Widget';

Matter.Common.setDecomp(decomp);

const MAX_SIZE = 200;
const PADDING = 100;
const CANVAS_SIZE = MAX_SIZE + PADDING * 2;
const N = 24;
const epsilon = 1e-4;
const directions = Array.from({length: N}, (_, i) => {
	const angle = 2 * Math.PI * i / N;
	return [Math.cos(angle), Math.sin(angle)];
});

function computeCentroid(vertices) {
	let area = 0, cx = 0, cy = 0;
	const count = vertices.length;
	for(let i = 0; i < count; i++) {
		const p1 = vertices[i];
		const p2 = vertices[(i + 1) % count];
		const cross = p1.x * p2.y - p2.x * p1.y;
		area += cross;
		cx += (p1.x + p2.x) * cross;
		cy += (p1.y + p2.y) * cross;
	}
	area *= 0.5;
	cx /= (6 * area);
	cy /= (6 * area);
	return { x: cx, y: cy };
}

export default class EmoteWallWidget extends Widget {
	constructor({ container, api, socket, hash, baseUrl }) {
		super({ container, api, socket, hash });
		this.baseUrl = baseUrl || '';

		this.engine = null;
		this.world = null;
		this.ctx = null;
		this.hullCache = {};
		this._animFrameId = null;
		this._lastTime = performance.now();
		this._runner = null;
		this._bounds = [];
		this._resizeObserver = null;
	}

	get _widgetType() { return 'emote-wall'; }

	async init() {
		this._startAnimLoop();
		await super.init();
	}

	async loadConfig() {
		await Promise.all([
			super.loadConfig(),
			this.loadGiftTypes(),
		]);
	}

	applySettings(settings) {
		const prev = this.settings;
		if(prev && prev !== settings && !!prev.physics !== !!settings.physics) {
			this._destroyPhysics();
			for(const el of Array.from(this.container.querySelectorAll('.bounce'))) el.remove();
		}
		super.applySettings(settings);
		if(settings.physics)
			this._initPhysics();
		else if(this.world)
			this.world.gravity.y = parseFloat(settings.gravity || 0) || 0;
		if(this.world && prev && (
			prev.emoteSize !== settings.emoteSize ||
			prev.stickerSize !== settings.stickerSize
		)) {
			this._rescaleObjects(settings);
		}
	}

	_destroyPhysics() {
		if(this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
		if(this.engine) {
			if(this._runner) {
				Matter.Runner.stop(this._runner);
				this._runner = null;
			}
			Matter.Engine.clear(this.engine);
			this.engine = null;
			this.world = null;
			this._bounds = [];
		}
		this.container.querySelector('canvas')?.remove();
		this.ctx = null;
	}

	_socketHandlers() {
		return {
			chat: msg => {
				if(msg.sender?.isSelf) return;
				if(!msg.spans) return;
				const emotes = this._extractEmotes(msg);
				if(emotes.length) this.spawnEmotes(emotes);
			},
		};
	}

	destroy() {
		if(this._animFrameId != null) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
		this._destroyPhysics();
		document.getElementById(`emoteWallStyle-${this.container.id}`)?.remove();
		super.destroy();
	}

	_generateCSS() {
		document.getElementById(`emoteWallStyle-${this.container.id}`)?.remove();

		const s = this.settings;
		const id = this.container.id;
		const css = `
			:root {
				--animate-duration: 200ms;
				--emote-size: ${(s.emoteSize || 5) * 10}px;
				--sticker-size: ${(s.stickerSize || 5) * 10}px;
			}
			#${id} .bounce {
				position: absolute;
				will-change: transform;
				pointer-events: none;
				opacity: 0;
				transition: opacity 200ms;
			}
			#${id} .emote {
				width: var(--emote-size);
				height: var(--emote-size);
				object-fit: contain;
				text-align: center;
			}
			#${id} .sticker {
				width: var(--sticker-size);
				height: var(--sticker-size);
				object-fit: contain;
			}
			#${id} .emoji {
				font-size: var(--emote-size);
				line-height: var(--emote-size);
			}
			#${id} canvas {
				z-index: 50;
				position: absolute;
			}
			#${id} img, #${id} span {
				z-index: 100;
			}
		`;

		const style = document.createElement('style');
		style.type = 'text/css';
		style.id = `emoteWallStyle-${id}`;
		style.appendChild(document.createTextNode(css));
		(document.head || document.getElementsByTagName('head')[0]).appendChild(style);
	}

	_rescaleObjects(settings) {
		for(const elem of this.container.querySelectorAll('.bounce')) {
			if(!elem.matterBody || !elem.physicsSize) continue;
			const newSize = elem.classList.contains('sticker')
				? (settings.stickerSize || 5) * 10
				: (settings.emoteSize || 5) * 10;
			if(newSize === elem.physicsSize) continue;
			const scale = newSize / elem.physicsSize;
			Matter.Body.scale(elem.matterBody, scale, scale);
			if(elem.ox != null) elem.ox *= scale;
			if(elem.oy != null) elem.oy *= scale;
			elem.physicsSize = newSize;
		}
	}

	_initPhysics() {
		if(this.engine) {
			this.world.gravity.y = parseFloat(this.settings.gravity || 0) || 0;
			return;
		}

		this.engine = Matter.Engine.create();
		this.world = this.engine.world;

		if(this.settings.debug) {
			Matter.Render.run(Matter.Render.create({
				element: this.container,
				engine: this.engine,
				options: {
					width: this.container.clientWidth,
					height: this.container.clientHeight,
					wireframes: true,
					background: 'transparent',
				},
			}));
		}

		this._runner = Matter.Runner.create();
		Matter.Runner.run(this._runner, this.engine);

		this._updateBounds(this.container.clientWidth, this.container.clientHeight);

		this._resizeObserver = new ResizeObserver(entries => {
			const { width, height } = entries[0].contentRect;
			this._updateBounds(width, height);
		});
		this._resizeObserver.observe(this.container);

		if(this.settings.shapes) {
			const shapes = this.settings.shapes.split('\n')
				.map(s => s.split(' ').filter(a => a).map(a => a.split(','))
				.filter(a => a.length === 2).map(a => ({ x: parseFloat(a[0]), y: parseFloat(a[1]) })));
			for(const shape of shapes) {
				if(shape.length < 3) continue;
				const centroid = computeCentroid(shape);
				Matter.Composite.add(this.world, Matter.Bodies.fromVertices(centroid.x, centroid.y, shape, { isStatic: true }));
			}
		}

		const canvas = document.createElement('canvas');
		if(!this.settings.debug) canvas.style.visibility = 'hidden';
		canvas.width = CANVAS_SIZE;
		canvas.height = CANVAS_SIZE;
		this.container.appendChild(canvas);
		this.ctx = canvas.getContext('2d', { willReadFrequently: true, desynchronized: true });

		this.world.gravity.y = parseFloat(this.settings.gravity || 0) || 0;
	}

	_updateBounds(width, height) {
		if(!this.world) return;
		for(const b of this._bounds) Matter.Composite.remove(this.world, b);
		const t = 300;
		this._bounds = [
			Matter.Bodies.rectangle(width / 2,      -t / 2,          width + t * 2, t, { isStatic: true }), // top
			Matter.Bodies.rectangle(width / 2,      height + t / 2,  width + t * 2, t, { isStatic: true }), // bottom
			Matter.Bodies.rectangle(-t / 2,         height / 2,      t, height + t * 2, { isStatic: true }), // left
			Matter.Bodies.rectangle(width + t / 2,  height / 2,      t, height + t * 2, { isStatic: true }), // right
		];
		Matter.Composite.add(this.world, this._bounds);
	}

	_getImageHull(img, sz, imgId) {
		const cacheId = `${imgId}-${sz}`;
		if(this.hullCache[cacheId]) return this.hullCache[cacheId];

		let scale = Math.min(MAX_SIZE / img.naturalWidth, MAX_SIZE / img.naturalHeight);
		let ew = Math.floor(img.naturalWidth * scale);
		let eh = Math.floor(img.naturalHeight * scale);
		let ex = PADDING + (MAX_SIZE - ew) / 2;
		let ey = PADDING + (MAX_SIZE - eh) / 2;

		this.ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
		if(img.tagName === 'IMG') {
			this.ctx.drawImage(img, ex, ey, ew, eh);
		} else if(img.tagName === 'SPAN') {
			this.ctx.font = `${MAX_SIZE}px Arial`;
			this.ctx.fillStyle = 'black';
			this.ctx.textAlign = 'left';
			this.ctx.textBaseline = 'ideographic';
			this.ctx.fillText(img.innerText, PADDING, PADDING + MAX_SIZE);
			ex = 0; ey = 0; ew = CANVAS_SIZE; eh = CANVAS_SIZE;
		}

		const { data } = this.ctx.getImageData(ex, ey, ew, eh);
		const dists = new Array(N).fill(Infinity);
		let idx = 3;
		for(let y = ey; y < ey + eh; y++) {
			let minX = CANVAS_SIZE + 1;
			let maxX = -1;
			for(let x = ex; x < ex + ew; x++) {
				const alpha = data[idx];
				idx += 4;
				if(alpha < 128) continue;
				if(x < minX) minX = x;
				if(x > maxX) maxX = x;
			}
			if(minX > maxX) continue;
			for(let i = 0; i < N; i++) {
				const [dx, dy] = directions[i];
				const ydy = (y - PADDING) * dy;
				const proj = (minX - PADDING) * dx + ydy;
				if(proj < dists[i]) dists[i] = proj;
				const proj2 = (maxX - PADDING) * dx + ydy;
				if(proj2 < dists[i]) dists[i] = proj2;
			}
		}

		const points = [];
		for(let i = 0; i < N; i++) {
			const j = (i + 1) % N;
			const [nx1, ny1] = directions[i];
			const [nx2, ny2] = directions[j];
			const d1 = dists[i];
			const d2 = dists[j];
			if(d1 === Infinity || d2 === Infinity) continue;

			const det = nx1 * ny2 - ny1 * nx2;
			if(Math.abs(det) < 1e-6) continue;

			let x = (d1 * ny2 - d2 * ny1) / det;
			let y = (nx1 * d2 - nx2 * d1) / det;

			const last = points[points.length - 1];
			if(last && (x - last.x) ** 2 + (y - last.y) ** 2 < epsilon ** 2) continue;
			if(i === N - 1 && points.length > 0) {
				const first = points[0];
				if((x - first.x) ** 2 + (y - first.y) ** 2 < epsilon ** 2) continue;
			}

			x *= sz / MAX_SIZE;
			y *= sz / MAX_SIZE;
			points.push({ x, y });
		}

		if(points.length > 0) this.hullCache[cacheId] = points;
		return points;
	}

	_extractEmotes(msg) {
		const emotes = [];
		if(msg.giftIcon) {
			for(let i = 0; i < (msg.multiplier || 1); i++)
				emotes.push({ type: 'sticker', src: msg.giftIcon });
		} else if(this.giftTypes?.[msg.serviceId]?.[msg?.giftType]) {
			const donoSettings = this.giftTypes[msg.serviceId][msg.giftType];
			let images;
			if(donoSettings.images?.length > 0) images = donoSettings.images;
			else if(donoSettings.image) images = [donoSettings.image];
			if(images?.length > 0) {
				for(let i = 0; i < (msg.multiplier || 1); i++)
					emotes.push({ type: 'sticker', src: util.randomItem(images.map(i => i.url)) });
			}
		}
		for(const span of msg.spans) {
			if(typeof span === 'string') {
				if(this.settings.showEmojis)
					for(const emoji of util.extractEmojis(span))
						emotes.push({ emoji, type: 'emoji' });
			} else if(span.type === 'text') {
				if(this.settings.showEmojis)
					for(const emoji of util.extractEmojis(span.text))
						emotes.push({ emoji, type: 'emoji' });
			} else if(span.type === 'emote') {
				if(!this.settings.customEmotesOnly || span.isCustom)
					emotes.push({ src: span.src, type: 'emote' });
			} else if(span.type === 'sticker') {
				emotes.push({ src: span.src, type: 'sticker' });
			}
		}
		return emotes;
	}

	_evictOldest() {
		const oldest = this.container.querySelector('.bounce');
		if(!oldest) return;
		if(oldest.matterBody) Matter.Composite.remove(this.world, oldest.matterBody);
		oldest.remove();
	}

	async spawnEmotes(emotes) {
		const startX = Math.round(Math.random() * this.container.clientWidth);
		const startY = Math.round(Math.random() * this.container.clientHeight);
		const limit = this.settings.objectLimit > 0 ? this.settings.objectLimit : Infinity;

		for(const emote of emotes) {
			while(this.container.querySelectorAll('.bounce').length >= limit) this._evictOldest();
			if(this.settings.physics) {
				let img, imgId;
				if(emote.type === 'emoji') {
					img = document.createElement('span');
					img.innerText = imgId = emote.emoji;
				} else {
					if(this.settings.customEmotesOnly)
					imgId = emote.src;
					const src = this.settings.physicsHulls ? `${this.baseUrl}/proxy/${encodeURIComponent(emote.src)}` : emote.src;
					img = await util.preloadImage(src, { crossOrigin: 'anonymous' });
				}
				if(!img) continue;

				img.className = `bounce ${emote.type}`;
				img.style.transformOrigin = 'top left';
				this.container.appendChild(img);
				requestAnimationFrame(() => requestAnimationFrame(() => { img.style.opacity = '1'; }));

				const scaledWidth = img.offsetWidth;
				const scaledHeight = img.offsetHeight;
				const imgSize = emote.type === 'sticker'
					? (this.settings.stickerSize || 5) * 10
					: (this.settings.emoteSize || 5) * 10;

				let body;
				try {
					if(!this.settings.physicsHulls) throw 'Hull computation disabled';
					const hull = this._getImageHull(img, imgSize, imgId);
					if(hull.length < 3) throw 'Invalid hull';
					const centroid = computeCentroid(hull);
					img.ox = centroid.x;
					img.oy = centroid.y;
					for(let n = 0; n < 5; n++) {
						const sx = Math.random() * (this.container.clientWidth - scaledWidth) + scaledWidth / 2;
						const sy = Math.random() * (this.container.clientHeight - scaledHeight) + scaledHeight / 2;
						body = Matter.Bodies.fromVertices(sx, sy, hull, {
							restitution: 0.9, friction: 0.05, frictionAir: 0.00,
							angle: Math.random() * Math.PI,
						});
						if(!body) throw new Error('Could not create body from vertices');
						if(Matter.Query.collides(body, this.world.bodies).length === 0) break;
					}
				} catch(e) {
					if(e !== 'Hull computation disabled') console.error(e);
					img.ox = scaledWidth / 2;
					img.oy = scaledHeight / 2;
					for(let n = 0; n < 5; n++) {
						const sx = Math.floor(Math.random() * (this.container.clientWidth - scaledWidth) + scaledWidth / 2);
						const sy = Math.floor(Math.random() * (this.container.clientHeight - scaledHeight) + scaledHeight / 2);
						const scale = Math.min(imgSize / scaledWidth, imgSize / scaledHeight);
						body = Matter.Bodies.rectangle(sx, sy, scaledWidth * scale, scaledHeight * scale, {
							restitution: 0.9, friction: 0.05, frictionAir: 0.00,
							angle: Math.random() * Math.PI,
						});
						if(!body) throw new Error('Could not create body from vertices');
						if(Matter.Query.collides(body, this.world.bodies).length === 0) break;
					}
				}

				Matter.Body.setVelocity(body, {
					x: (Math.random() - 0.5) * 20,
					y: (Math.random() - 0.5) * 20,
				});
				Matter.Composite.add(this.world, body);
				img.matterBody = body;
				img.physicsSize = imgSize;

				setTimeout(() => {
					img.style.opacity = '0';
					img.addEventListener('transitionend', () => {
						if(img.matterBody) {
							Matter.Composite.remove(this.world, img.matterBody);
							img.matterBody = null;
						}
						img.remove();
					});
				}, this.settings.duration * 1000 - 200);
			} else {
				let img;
				if(emote.type === 'emoji') {
					img = document.createElement('span');
					img.innerText = emote.emoji;
				} else {
					img = document.createElement('img');
					img.src = emote.src;
				}
				img.className = `bounce ${emote.type}`;
				this.container.appendChild(img);
				requestAnimationFrame(() => requestAnimationFrame(() => { img.style.opacity = '1'; }));

				img.style.left = `${startX}px`;
				img.style.top = `${startY}px`;
				img.dx = (Math.random() - 0.5) * 300;
				img.dy = (Math.random() - 0.5) * 300;

				setTimeout(() => {
					img.style.opacity = '0';
					img.addEventListener('transitionend', () => img.remove());
				}, this.settings.duration * 1000 - 200);
			}
		}
	}

	_startAnimLoop() {
		this._lastTime = performance.now();
		const animate = (now) => {
			this._tick((now - this._lastTime) / 1000);
			this._lastTime = now;
			this._animFrameId = requestAnimationFrame(animate);
		};
		this._animFrameId = requestAnimationFrame(animate);
	}

	_tick(dt) {
		if(!this.settings) return;
		for(const elem of this.container.childNodes) {
			if(this.settings.physics) {
				if(elem.tagName == 'CANVAS') continue;
				if(!elem.matterBody) continue;
				elem.style.transform = `
					translate(${elem.matterBody.position.x}px, ${elem.matterBody.position.y}px)
					rotate(${elem.matterBody.angle}rad)
					translate(${-elem.ox}px, ${-elem.oy}px)
				`;
			} else {
				if(!('dx' in elem)) continue;
				let x = elem.offsetLeft;
				let y = elem.offsetTop;
				x += dt * elem.dx;
				y += dt * elem.dy;

				const w = this.container.clientWidth;
				const h = this.container.clientHeight;

				if(x < 0 || x > w - elem.offsetWidth) {
					elem.dx *= -1;
					x = Math.max(0, Math.min(x, w - elem.offsetWidth));
				}
				if(y < 0 || y > h - elem.offsetHeight) {
					elem.dy *= -1;
					y = Math.max(0, Math.min(y, h - elem.offsetHeight));
				}

				elem.style.left = `${x}px`;
				elem.style.top = `${y}px`;
			}
		}
	}
}
